import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../components/AuthProvider';
import {
  DOCUMENT_TYPE_LABEL,
  getDocumentUrl,
  listDocumentsWithAlerts,
  listWorkerDocuments,
  uploadWorkerDocument,
  type DocumentType,
  type DocumentWithAlert,
  type WorkerDocumentRow,
  type WorkerDocuments,
} from '../../api/documents';
import FilaTarjeta from '../../components/FilaTarjeta';
import { coincideTodasLasPalabras } from '../../domain/tableFilters';
import { rechazoDeArchivo } from '../../lib/archivoDeFoto';

/** 2026-08-08 → 08/08/2026 */
const dmy = (d: string | null) => (d ? d.split('-').reverse().join('/') : '—');

const docTitulo = (d: DocumentWithAlert) => d.label ?? DOCUMENT_TYPE_LABEL[d.type];

function EstadoDoc({ doc }: { doc: DocumentWithAlert }) {
  if (!doc.expiry_date) {
    return <span className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-700">Sin vencimiento</span>;
  }
  if (!doc.alert) {
    return <span className="rounded bg-green-100 px-2 py-0.5 text-xs text-green-800">Vigente</span>;
  }
  if (doc.alert.severity === 'vencido') {
    const n = Math.abs(doc.alert.daysLeft);
    return (
      <span className="rounded bg-red-100 px-2 py-0.5 text-xs text-red-800">
        Vencido hace {n} {n === 1 ? 'día' : 'días'}
      </span>
    );
  }
  const n = doc.alert.daysLeft;
  return (
    <span className="rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-800">
      Vence en {n} {n === 1 ? 'día' : 'días'}
    </span>
  );
}

interface Fila {
  key: string;
  worker: WorkerDocuments;
  doc: DocumentWithAlert | null;
}

export default function Documentos() {
  const { roles } = useAuth();
  const isAdmin = roles.includes('admin');
  const [workers, setWorkers] = useState<WorkerDocuments[]>([]);
  const [soloAlerta, setSoloAlerta] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [modalWorker, setModalWorker] = useState<WorkerDocuments | null>(null);

  const recargar = useCallback(() => {
    setLoading(true);
    listDocumentsWithAlerts()
      .then((rows) => setWorkers(rows))
      .catch((e) => setMessage(e instanceof Error ? e.message : 'Error al cargar los documentos.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(recargar, [recargar]);

  const filas = useMemo<Fila[]>(() => {
    const out: Fila[] = [];
    for (const w of workers) {
      if (w.documents.length === 0) {
        out.push({ key: w.worker_id, worker: w, doc: null });
        continue;
      }
      for (const d of w.documents) out.push({ key: d.id, worker: w, doc: d });
    }
    const conAlerta = soloAlerta
      ? out.filter((f) => f.doc === null || f.doc.alert !== null)
      : out;
    /**
     * Se busca sobre los MISMOS campos que la fila muestra: nombre, documento de identidad, nombre
     * del documento laboral, campaña y tienda. Es el helper que ya usan Validación y la bandeja de
     * cierres, así que «Huamani sanidad» funciona igual en las tres pantallas: todas las palabras,
     * en cualquier campo, sin importar tildes ni mayúsculas.
     */
    return conAlerta.filter((f) => coincideTodasLasPalabras(
      busqueda,
      f.worker.first_names,
      f.worker.last_names,
      f.worker.dni,
      f.doc === null ? 'sin documentos registrados' : docTitulo(f.doc),
      f.worker.campanias.join(' '),
      f.worker.tiendas.join(' '),
    ));
  }, [workers, soloAlerta, busqueda]);

  /** Las filas ANTES del filtro de texto: es el «de M» del contador. */
  const totalFilas = useMemo(
    () => workers.reduce((n, w) => n + Math.max(1, w.documents.length), 0),
    [workers],
  );

  const totalAlertas = useMemo(
    () => workers.reduce((n, w) => n + w.alerts.length, 0),
    [workers],
  );

  /**
   * Abre el documento en otra pestaña.
   *
   * Dos fallos distintos, dos mensajes distintos, porque la salida es distinta:
   *
   *  - **Sin permiso**: `createSignedUrl` falla y `getDocumentUrl` lanza. Es lo que le pasaba al
   *    validador de datos hasta la 0106, y el mensaje del servidor se muestra tal cual.
   *  - **Ventana bloqueada**: `window.open` devuelve `null` porque el navegador ya no considera
   *    esto un gesto del usuario —la firma de la URL mete un `await` en el medio— y lo trata como
   *    un pop-up. El documento existe y el permiso está: lo único que falta es permitir la
   *    ventana. Sin este caso, un bloqueo de pop-ups se lee otra vez como «no pasa nada».
   */
  async function verArchivo(path: string) {
    setMessage('');
    try {
      const url = await getDocumentUrl(path);
      const ventana = window.open(url, '_blank');
      if (!ventana) {
        setMessage('El navegador bloqueó la ventana nueva. Permite las ventanas emergentes de '
          + 'este sitio y vuelve a tocar «Ver archivo».');
      }
    } catch (e) {
      setMessage(e instanceof Error ? e.message : 'No se pudo abrir el documento.');
    }
  }

  return (
    <div className="p-6">
      <h1 className="mb-1 text-2xl font-semibold">Documentos laborales</h1>
      <p className="mb-4 text-sm text-gray-500">
        {totalAlertas === 0
          ? 'Ningún documento vencido o por vencer.'
          : `${totalAlertas} documento(s) vencido(s) o por vencer.`}
        {!isAdmin && ' Solo el jefe de operaciones puede subir o renovar documentos.'}
      </p>

      <div className="mb-4 flex flex-wrap items-center gap-4">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={soloAlerta}
            onChange={(e) => setSoloAlerta(e.target.checked)}
          />
          Solo con alerta (vencidos, por vencer o sin documentos)
        </label>
        <button onClick={recargar} className="rounded border px-3 py-1 text-sm">
          Actualizar
        </button>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <label className="flex flex-col text-sm">
          Nombre, documento, tienda o campaña
          <input
            type="search"
            className="w-72 rounded border border-gray-300 px-2 py-1"
            placeholder="Ej.: Huamani, 46053436, sanidad, Bimbo, Makro Surco"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
          />
        </label>
        {busqueda.trim() !== '' && (
          <span className="text-sm text-gray-600">
            Mostrando {filas.length} de {totalFilas}
          </span>
        )}
      </div>

      {/*
        El aviso va ACÁ, en bloque y en rojo, y no como un texto gris al lado del filtro.
        Es el arreglo de lo que el usuario reportó el 10-set: «Ver archivo» clickea y no pasa nada.
        Lo que pasaba es que el error SÍ se mostraba —pero arriba, en gris, a cuarenta filas del
        enlace que se había tocado—. Con la tabla desplazada o desde el celular, eso es
        indistinguible de un botón muerto.
      */}
      {message && (
        <p role="alert" className="mb-4 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          {message}
        </p>
      )}

      {/* En celular: una tarjeta por documento (sin desplazamiento horizontal). */}
      <div className="space-y-2 lg:hidden">
        {filas.map((f) => (
          <FilaTarjeta
            key={f.key}
            titulo={`${f.worker.first_names} ${f.worker.last_names}`}
            estados={
              f.doc === null ? (
                <span className="rounded bg-gray-200 px-2 py-0.5 text-xs text-gray-800">
                  Sin documentos registrados
                </span>
              ) : (
                <EstadoDoc doc={f.doc} />
              )
            }
            campos={[
              { etiqueta: 'DNI', valor: f.worker.dni },
              f.worker.campanias.length === 0 ? null
                : { etiqueta: 'Campaña', valor: f.worker.campanias.join(' · ') },
              f.worker.tiendas.length === 0 ? null
                : { etiqueta: 'Tienda', valor: f.worker.tiendas.join(' · ') },
              f.doc !== null && { etiqueta: 'Documento', valor: docTitulo(f.doc) },
              f.doc?.issue_date ? { etiqueta: 'Emisión', valor: dmy(f.doc.issue_date) } : null,
              f.doc?.expiry_date ? { etiqueta: 'Vencimiento', valor: dmy(f.doc.expiry_date) } : null,
            ]}
            acciones={
              <>
                {f.doc !== null && (
                  <button
                    onClick={() => verArchivo(f.doc!.file_path)}
                    className="text-blue-600 underline"
                  >
                    Ver archivo
                  </button>
                )}
                {isAdmin && (
                  <button
                    onClick={() => setModalWorker(f.worker)}
                    className="rounded bg-blue-600 px-3 py-1 text-xs text-white"
                  >
                    {f.doc === null ? 'Subir documento' : 'Renovar'}
                  </button>
                )}
              </>
            }
          />
        ))}
        {!loading && filas.length === 0 && (
          <p className="rounded border bg-white p-4 text-center text-sm text-gray-500">
            {soloAlerta
              ? 'Ningún trabajador con alerta de documentos.'
              : 'No hay trabajadores activos registrados.'}
          </p>
        )}
        {loading && (
          <p className="rounded border bg-white p-4 text-center text-sm text-gray-500">Cargando…</p>
        )}
      </div>

      <div className="hidden overflow-x-auto lg:block">
        <table className="min-w-full border text-sm">
          <thead className="bg-gray-100">
            <tr className="text-left">
              <th className="p-2">Trabajador</th>
              <th className="p-2">DNI</th>
              <th className="p-2">Documento</th>
              <th className="p-2">Emisión</th>
              <th className="p-2">Vencimiento</th>
              <th className="p-2">Estado</th>
              <th className="p-2">Archivo</th>
              <th className="p-2">Acción</th>
            </tr>
          </thead>
          <tbody>
            {filas.map((f) => (
              <tr key={f.key} className="border-t">
                <td className="p-2">
                  {f.worker.first_names} {f.worker.last_names}
                  {/* Se muestran porque el filtro busca acá: una fila que calza por un dato que no
                      está a la vista parece un error de la pantalla. */}
                  {(f.worker.campanias.length > 0 || f.worker.tiendas.length > 0) && (
                    <p className="text-xs text-gray-500">
                      {[...f.worker.campanias, ...f.worker.tiendas].join(' · ')}
                    </p>
                  )}
                </td>
                <td className="p-2">{f.worker.dni}</td>
                {f.doc === null ? (
                  <>
                    <td className="p-2 text-gray-500" colSpan={4}>
                      <span className="rounded bg-gray-200 px-2 py-0.5 text-xs text-gray-800">
                        Sin documentos registrados
                      </span>
                    </td>
                    <td className="p-2 text-gray-400">—</td>
                  </>
                ) : (
                  <>
                    <td className="p-2">{docTitulo(f.doc)}</td>
                    <td className="p-2">{dmy(f.doc.issue_date)}</td>
                    <td className="p-2">{dmy(f.doc.expiry_date)}</td>
                    <td className="p-2">
                      <EstadoDoc doc={f.doc} />
                    </td>
                    <td className="p-2">
                      <button
                        onClick={() => verArchivo(f.doc!.file_path)}
                        className="text-blue-600 underline"
                      >
                        Ver archivo
                      </button>
                    </td>
                  </>
                )}
                <td className="p-2">
                  {isAdmin ? (
                    <button
                      onClick={() => setModalWorker(f.worker)}
                      className="rounded bg-blue-600 px-3 py-1 text-xs text-white"
                    >
                      {f.doc === null ? 'Subir documento' : 'Renovar'}
                    </button>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </td>
              </tr>
            ))}
            {!loading && filas.length === 0 && (
              <tr>
                <td colSpan={8} className="p-4 text-center text-gray-500">
                  {soloAlerta
                    ? 'Ningún trabajador con alerta de documentos.'
                    : 'No hay trabajadores activos registrados.'}
                </td>
              </tr>
            )}
            {loading && (
              <tr>
                <td colSpan={8} className="p-4 text-center text-gray-500">
                  Cargando…
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {modalWorker && (
        <ModalDocumento
          worker={modalWorker}
          onClose={() => setModalWorker(null)}
          onSaved={(msg) => {
            setModalWorker(null);
            setMessage(msg);
            recargar();
          }}
        />
      )}
    </div>
  );
}

const TIPOS: DocumentType[] = ['carnet_sanidad', 'certificado_unico_laboral', 'otro'];

function ModalDocumento({
  worker,
  onClose,
  onSaved,
}: {
  worker: WorkerDocuments;
  onClose: () => void;
  onSaved: (msg: string) => void;
}) {
  const [type, setType] = useState<DocumentType>('carnet_sanidad');
  const [label, setLabel] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [issueDate, setIssueDate] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [historial, setHistorial] = useState<WorkerDocumentRow[]>([]);

  useEffect(() => {
    listWorkerDocuments(worker.worker_id)
      .then(setHistorial)
      .catch(() => setHistorial([]));
  }, [worker.worker_id]);

  async function guardar() {
    if (!file) {
      setError('Selecciona el archivo del documento (imagen o PDF).');
      return;
    }
    if (type === 'otro' && label.trim() === '') {
      setError('Escribe el nombre del documento.');
      return;
    }
    if (type !== 'otro' && expiryDate === '') {
      setError('Ingresa la fecha de vencimiento para poder alertar antes de que caduque.');
      return;
    }
    if (issueDate !== '' && expiryDate !== '' && expiryDate < issueDate) {
      setError('La fecha de vencimiento no puede ser anterior a la de emisión.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      await uploadWorkerDocument(
        worker.worker_id,
        worker.dni,
        type,
        file,
        issueDate,
        expiryDate,
        label,
      );
      onSaved(
        `Documento registrado para ${worker.first_names} ${worker.last_names}. ` +
          'El anterior del mismo tipo quedó archivado.',
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo subir el documento.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded bg-white p-4 shadow-lg">
        <h2 className="mb-1 font-semibold">Renovar / subir documento</h2>
        <p className="mb-3 text-sm text-gray-500">
          {worker.first_names} {worker.last_names} — DNI {worker.dni}
        </p>

        <label className="mb-2 block text-sm">
          Tipo de documento
          <select
            className="mt-1 w-full rounded border px-2 py-1"
            value={type}
            onChange={(e) => setType(e.target.value as DocumentType)}
          >
            {TIPOS.map((t) => (
              <option key={t} value={t}>
                {DOCUMENT_TYPE_LABEL[t]}
              </option>
            ))}
          </select>
        </label>

        {type === 'otro' && (
          <label className="mb-2 block text-sm">
            Nombre del documento
            <input
              type="text"
              className="mt-1 w-full rounded border px-2 py-1"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Ej.: Certificado de manipulación de alimentos"
            />
          </label>
        )}

        <label className="mb-2 block text-sm">
          Archivo (imagen o PDF)
          <input
            type="file"
            accept="image/*,application/pdf"
            className="mt-1 w-full text-sm"
            onChange={(e) => {
              // Tipo y tope del bucket (0314). El PDF se sube tal cual, así que su tamaño es el
              // que llega al servidor; una imagen se comprime, pero si no se puede viaja entera.
              const f = e.target.files?.[0] ?? null;
              const motivo = f === null ? null : rechazoDeArchivo(f, { pdf: true });
              if (motivo !== null) {
                setError(motivo);
                setFile(null);
                e.target.value = '';
                return;
              }
              setError('');
              setFile(f);
            }}
          />
        </label>

        <div className="mb-3 flex gap-3">
          <label className="flex-1 text-sm">
            Fecha de emisión
            <input
              type="date"
              className="mt-1 w-full rounded border px-2 py-1"
              value={issueDate}
              onChange={(e) => setIssueDate(e.target.value)}
            />
          </label>
          <label className="flex-1 text-sm">
            Fecha de vencimiento
            <input
              type="date"
              className="mt-1 w-full rounded border px-2 py-1"
              value={expiryDate}
              onChange={(e) => setExpiryDate(e.target.value)}
            />
          </label>
        </div>

        {error && <p className="mb-2 text-sm text-red-600">{error}</p>}

        <div className="mb-4 flex justify-end gap-2">
          <button onClick={onClose} className="rounded border px-3 py-1 text-sm" disabled={busy}>
            Cancelar
          </button>
          <button
            onClick={guardar}
            disabled={busy}
            className="rounded bg-blue-600 px-3 py-1 text-sm text-white disabled:opacity-50"
          >
            {busy ? 'Subiendo…' : 'Guardar documento'}
          </button>
        </div>

        <h3 className="mb-1 text-sm font-semibold">Historial del trabajador</h3>
        {historial.length === 0 ? (
          <p className="text-sm text-gray-500">Sin documentos registrados.</p>
        ) : (
          <ul className="text-sm">
            {historial.map((h) => (
              <li key={h.id} className="border-t py-1">
                {h.label ?? DOCUMENT_TYPE_LABEL[h.type]} — vence {dmy(h.expiry_date)}{' '}
                {h.active ? (
                  <span className="text-green-700">(vigente)</span>
                ) : (
                  <span className="text-gray-400">(archivado)</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
